import { useEffect, useState } from "react";
import { getBaselineDraft } from "@/api";

const QLABEL = {
  q1: "Did it name you or your work specifically?",
  q2: "Did it use the specific words or approach only you use?",
  q3: "Or did it default to generic advice for the broader category?",
  q4: "Did it name other specific people, brands, or tools as the go-to answer?",
};

// CB-4B: read-only presentation of the cycle's confirmed Day 0 Baseline + preserved search phrases,
// sourced from the canonical cycle records (GET /cycle/baseline). No /findability-check/* calls, no
// regeneration, no edits — the confirmed Baseline is immutable here. Day 30 Progress Review display
// is intentionally deferred to a later phase (read gap flagged).
export default function CycleFindabilityPanel() {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(-1);

  useEffect(() => {
    let cancelled = false;
    getBaselineDraft()
      .then((d) => { if (!cancelled) setDraft(d); })
      .catch(() => { if (!cancelled) setDraft(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="tab-pane" data-testid="cycle-findability-loading"><div className="empty-state">Loading your Baseline…</div></div>;
  }

  const phrases = (draft && draft.phrases) || [];
  const answers = (draft && draft.answers) || {};
  const confirmed = !!(draft && draft.confirmed);

  const copy = (t, i) => { navigator.clipboard?.writeText(t); setCopied(i); setTimeout(() => setCopied(-1), 1600); };

  return (
    <div className="tab-pane" id="tab-findability" role="tabpanel" data-testid="cycle-findability-panel">
      <h2 className="section-h2">Your Findability Baseline</h2>
      <p className="section-sub">
        This is your baseline measurement for this cycle — saved and locked. Your 30 focused efforts build on it.
      </p>

      <div className="what-box" data-testid="cycle-findability-status">
        {confirmed ? "✓ Baseline confirmed and locked for this cycle." : "Your Baseline is in progress."}
      </div>

      {phrases.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="panel-title" style={{ fontSize: "1rem" }}>Your search phrases</div>
          <div className="panel-sub">The phrases you tested in ChatGPT / Perplexity — preserved for your end-of-cycle comparison.</div>
          <div data-testid="cycle-findability-phrases" style={{ display: "grid", gap: 8, marginTop: 8 }}>
            {phrases.map((p, i) => (
              <div key={i} data-testid={`cycle-phrase-${i}`}
                   style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                            padding: "0.6rem 0.85rem", border: "1px solid var(--line, #e5e5e5)", borderRadius: 10 }}>
                <span>“{p}”</span>
                <button className="btn-tiny" data-testid={`cycle-phrase-copy-${i}`} onClick={() => copy(p, i)}>
                  {copied === i ? "Copied!" : "Copy"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {draft && draft.note && (
        <div style={{ marginTop: 16 }}>
          <div className="panel-title" style={{ fontSize: "1rem" }}>Baseline — what you found</div>
          <div className="post-text" data-testid="cycle-findability-note" style={{ marginTop: 6 }}>{draft.note}</div>
        </div>
      )}

      {Object.keys(answers).length > 0 && (
        <div style={{ marginTop: 16 }} data-testid="cycle-findability-answers">
          {["q1", "q2", "q3", "q4"].filter((k) => answers[k]).map((k) => (
            <div key={k} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: ".85rem", color: "var(--muted, #777)" }}>{QLABEL[k]}</div>
              <div style={{ fontWeight: 600, textTransform: "capitalize" }}>{answers[k]}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
